import * as React from 'react';
import { CompanyModal, CompanyModalInterface } from '../Modal/CompanyModal';
import { Button, ButtonType } from '../common/Button/Button';
import './CompanyDetails.scss';
import { l } from '../../Other/Translation';
import { InvoicerInterface } from '../../Interfaces/Draft/DraftInterface';
import Immutable from '../../Other/Immutable';
import { ContactInterface } from '../../Interfaces/Settings/TradeNameInterface';
import { InvoicerStub } from '../../Interfaces/__stubs__/InvoicerInterface.stubs';


export interface CompanyInfoProps {
  invoicer: InvoicerInterface;
  saveCompany: (company) => void;
}

const CompanyAddress = ({ address }) => {
  const city = address.city || address.zipCode ? `${address.zipCode || ''} ${address.city || ''}` : null;
  return <div className={'address'}>
    <div className={'row'}>{address.streetAddress1}</div>
    {address.streetAddress2 && <div className={'row'}>{address.streetAddress2}</div>}
    {city && <div className={'row'}>{city}</div>}
  </div>;
};

const CompanyTradeName = ({ tn }) => {
  if (!tn) {
    return null;
  }
  return (
    <>
      {tn.name && <div className={'companyName'}>{tn.name}</div>}
      {tn.contact && <div>{tn.contact.name.first} {tn.contact.name.last}</div>}
      {tn.address && <CompanyAddress address={tn.address} />}
    </>
  );
};

const keys = {
  header: 'Sender',
  addCompany: 'AddCompanyDetails',
  editCompany: 'Edit',
  vatNumber: 'vatNumber'
};

const mapInvoicerToCompany = (invoicer: InvoicerInterface): CompanyModalInterface => {
  if (!invoicer) {
    return {};
  }
  const company: CompanyModalInterface = {
    businessId: invoicer.businessId,
    vatNumber: invoicer.vatNumber,
    legalStatus: invoicer.legalStatus,
    tradeRegisterId: invoicer.rcs
  };
  if (invoicer.tradeName) {
    company.companyName = invoicer.tradeName.name;
    if (invoicer.tradeName.address) {
      company.streetAddress1 = invoicer.tradeName.address.streetAddress1;
      company.streetAddress2 = invoicer.tradeName.address.streetAddress2;
      company.zipCode = invoicer.tradeName.address.zipCode;
      company.city = invoicer.tradeName.address.city;
    }
    if (invoicer.tradeName.contact) {
      company.firstName = invoicer.tradeName.contact.name.first;
      company.lastName = invoicer.tradeName.contact.name.last;
      company.mobile = invoicer.tradeName.contact.mobilePhone;
      company.phone = invoicer.tradeName.contact.phone;
    }
    if (invoicer.tradeName.logo) {
      company.logo = invoicer.tradeName.logo.content;
    }
  }
  return company;
};

const mapCompanyToInvoicer = (invoicer: InvoicerInterface, company: CompanyModalInterface): InvoicerInterface => {
  const updatedInvoicer = invoicer ? Immutable.copy(invoicer) : InvoicerStub;
  if (updatedInvoicer.tradeName) {
    updatedInvoicer.tradeName.name = company.companyName;
    const address = updatedInvoicer.tradeName.address || {};
    address.streetAddress1 = company.streetAddress1;
    address.streetAddress2 = company.streetAddress2;
    address.city = company.city;
    address.zipCode = company.zipCode;
    address.pobox = company.zipCode; // this because the preview still uses pobox instead of zipCode.
    updatedInvoicer.tradeName.address = address;
    const contact: ContactInterface = updatedInvoicer.tradeName.contact || {};
    contact.name = {
      first: company.firstName || '',
      last: company.lastName || '',
      full: `${company.firstName || ''} ${company.lastName || ''}`
    };
    contact.phone = company.phone;
    contact.mobilePhone = company.mobile;
    updatedInvoicer.tradeName.contact = contact;
    updatedInvoicer.tradeName.logo = { id: null, content: company.logo, name: 'logo' };
    if (company.businessId) { updatedInvoicer.businessId = company.businessId; }
  }
  updatedInvoicer.vatNumber = company.vatNumber;
  return updatedInvoicer;
};

export const CompanyDetails: React.FunctionComponent<CompanyInfoProps> = ({ invoicer, saveCompany }) => {

  const [isModalOpen, setModalOpen] = React.useState(false);
  return (
    <div className={'CompanyDetails'}>
      <CompanyModal
        isOpen={isModalOpen}
        company={mapInvoicerToCompany(invoicer)}
        save={(company) => saveCompany(mapCompanyToInvoicer(invoicer, company))}
        close={() => setModalOpen(false)}
      />
      <div className={'CompanyHeader'}>{l(keys.header)}</div>
      {invoicer ?
        <div className={'CompanyInfo'}>
          {invoicer.tradeName && <CompanyTradeName tn={invoicer.tradeName} />}
          <Button
            type={ButtonType.link}
            label={l(keys.editCompany)}
            action={() => setModalOpen(true)}
          />
        </div>
        :
        <div className={'CompanyAdd'}>
          <Button
            type={ButtonType.link}
            label={l(keys.addCompany)}
            action={() => setModalOpen(true)}
          />
        </div>
      }
    </div>
  );
}
